import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import {PricedItemGroupRefusal} from '../types/PricedItemGroupRefusal';
import {t} from './catalogueUi';
import {pricedItemGroupStore} from './pricedItemGroupStore';

const groupAddedMessage = () =>
  t(
    'entity.newVehicle.catalogue.itemGroup.added',
    'Added to the group. Nothing is saved until you confirm the group.'
  );

/**
 * Adds the entries to the open group of the year, all or none. Throws a PricedItemGroupRefusal
 * when the group refuses one of them (spec 7.2).
 */
const reserveInGroup = (yearId: string, entries: PricedItemBatchEntry[]) =>
  match(pricedItemGroupStore.add(yearId, entries))
    .with('added', () => undefined)
    .with(
      'targetTaken',
      'matrixConflict',
      'trimInMatrix',
      'valueTaken',
      'tooManyChanges',
      (reason) => {
        throw new PricedItemGroupRefusal(reason);
      }
    )
    .with({reason: 'trimSwitchWithNewItemRow'}, (refusal) => {
      throw new PricedItemGroupRefusal(refusal);
    })
    .exhaustive();

/** Adds the entries to the open group (see reserveInGroup) and tells the user nothing was sent. */
export const addToGroup = (yearId: string, entries: PricedItemBatchEntry[]) => {
  reserveInGroup(yearId, entries);
  showNotification.success(groupAddedMessage());
  return 'grouped' as const;
};

/**
 * The group part of writePricedItemChanges: the entries with rows are reserved in the group first
 * and taken back when the send of the entries without rows fails, so it is all or none. An entry
 * sent at once must not change a target the group holds, as the group would send a stale write.
 */
export const addEntriesToGroup = async (options: {
  yearId: string;
  entries: PricedItemBatchEntry[];
  withRows: PricedItemBatchEntry[];
  onEntryFailed?: (entryIndex: number) => void;
  writeBatch: (batch: {
    yearId: string;
    entries: PricedItemBatchEntry[];
    onEntryFailed?: (entryIndex: number) => void;
  }) => Promise<unknown>;
}) => {
  const {yearId, withRows} = options;
  const withoutRows = options.entries.filter((entry) => entry.items.length === 0);
  const groupedKeys = new Set(pricedItemGroupStore.get(yearId)?.entries.map(({key}) => key));
  if (withoutRows.some((entry) => groupedKeys.has(entry.key))) {
    throw new PricedItemGroupRefusal('targetTaken');
  }
  reserveInGroup(yearId, withRows);
  if (withoutRows.length > 0) {
    await options
      .writeBatch({
        yearId,
        entries: withoutRows,
        onEntryFailed: (entryIndex) =>
          options.onEntryFailed?.(options.entries.indexOf(withoutRows[entryIndex])),
      })
      .catch((error: unknown) => {
        pricedItemGroupStore.takeBack(yearId, withRows);
        throw error;
      });
  }
  showNotification.success(groupAddedMessage());
  return 'grouped' as const;
};
